import {navItems} from './utils'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {TAppLink, TLink} from '~/features/TLink'
import {MobileMenu} from './MobileMenu'
import {LanguageMenu} from '../LanguageMenu'
import {LaraLogoLink} from '../LaraLogoLink'
import {UserLocaleSuggestionDialog} from '../UserLocaleSuggestionDialog'
import {useLandingSettings} from '~/utils/cookies'
import SupportButton from './SupportButton'
import {useRootLoaderData} from '~/utils/useRootLoaderData'

const headerItemCommonStyle = clsx(
  'cursor-pointer rounded-sm px-4 py-2.5 text-sm font-medium transition-all',
  'hover:ring-1 hover:ring-neutral-25/3',
  'focus-visible:outline-0 focus-visible:ring-1 focus-visible:ring-neutral-25/3'
)

export const Header = () => {
  const {t} = useTranslation()
  const {isAuthenticated} = useRootLoaderData()

  const {
    settings: {isLanguageSuggestionDismissed},
    updateSettings
  } = useLandingSettings()

  const dismissLanguageSuggestion = () => updateSettings({isLanguageSuggestionDismissed: true})

  return (
    <header
      data-panda-theme="translated"
      data-panda-mode="light"
      className={clsx(
        'mx-auto grid w-full grid-cols-[auto_1fr] items-center text-neutral-25 md:grid-cols-[1fr_auto_1fr]',
        'px-3 py-4 transition-all duration-300 md:px-6 lg:px-8'
      )}
    >
      <LaraLogoLink />
      <div className="hidden items-center gap-1 md:flex">
        {navItems.map(item =>
          item?.isAppUrl ? (
            <TAppLink key={item.id} to={item.link} className={headerItemCommonStyle}>
              {t(item.label)}
            </TAppLink>
          ) : (
            <TLink
              isNav
              key={item.id}
              to={item.link}
              className={({isActive}) =>
                clsx(isActive ? 'ring-1 ring-neutral-25/2' : '', headerItemCommonStyle)
              }
            >
              {t(item.label)}
            </TLink>
          )
        )}
        <a
          href="https://developers.laratranslate.com"
          target="_blank"
          rel="noreferrer"
          className={headerItemCommonStyle}
        >
          {t('nav_pages.api_docs')}
        </a>
        <SupportButton className={headerItemCommonStyle} />
      </div>
      <div className="relative flex items-center justify-end gap-1 md:gap-4">
        <LanguageMenu dismissLanguageSuggestion={dismissLanguageSuggestion} />
        <div className="hidden items-center gap-1 md:flex">
          {isAuthenticated ? (
            <TAppLink
              aria-label="Go to app"
              to="/translate"
              className={clsx(
                'hidden bg-purple-400/2 [text-shadow:0px_0.125rem_0.125rem_rgba(0,0,0,0.32)] md:block',
                headerItemCommonStyle,
                'ring-1 ring-neutral-25/2'
              )}
            >
              {t('go_to_app')}
            </TAppLink>
          ) : (
            <>
              <TAppLink
                aria-label={t('login')}
                to="/login"
                className={clsx(
                  'max-lg:bg-purple-400/2 max-lg:[text-shadow:0px_0.125rem_0.125rem_rgba(0,0,0,0.32)]',
                  headerItemCommonStyle,
                  'ring-1 ring-neutral-25/2'
                )}
              >
                {t('login')}
              </TAppLink>
              <TAppLink
                aria-label={t('sign_up')}
                to="/sign-up"
                className={clsx(
                  'hidden bg-purple-400/2 [text-shadow:0px_0.125rem_0.125rem_rgba(0,0,0,0.32)] lg:block',
                  headerItemCommonStyle,
                  'ring-1 ring-neutral-25/2'
                )}
              >
                {t('sign_up')}
              </TAppLink>
            </>
          )}
        </div>
        <MobileMenu isAuthenticated={isAuthenticated} />
        <UserLocaleSuggestionDialog
          isLanguageSuggestionDismissed={isLanguageSuggestionDismissed}
          dismissLanguageSuggestion={dismissLanguageSuggestion}
        />
      </div>
    </header>
  )
}
